import { HttpErrorResponse } from '@angular/common/http';
import { timeout } from 'rxjs';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import {
  buildErrorReportRequest,
  buildErrorReportDiagnostics,
  ErrorReportAttachment,
  ErrorReportContext,
  ErrorReportingFacade,
  ErrorReportResponse,
  ErrorReportStore,
  MAX_REPORT_FILES,
  MAX_REPORT_FILE_BYTES,
  MAX_REPORT_TOTAL_FILE_BYTES,
} from '@imagise/entities';
import { ERROR_REPORT_FEEDBACK_AUTH } from '../../error-reporting.config';

type SubmitStatus = 'idle' | 'sending' | 'queued' | 'success' | 'error' | 'uncertain' | 'reported';
const REQUEST_TIMEOUT_MS = 30_000;
const DEFAULT_RETRY_SECONDS = 60;

/**
 * Reusable support-report form. Renders the (already redacted) technical context
 * read-only, collects a public message and optional guest e-mail, and submits
 * the report through {@link ErrorReportingFacade}. Used both on the recovery
 * page and inside the non-fatal overlay.
 */
@Component({
  selector: 'lib-error-report-form',
  standalone: true,
  imports: [ReactiveFormsModule, TranslateModule],
  templateUrl: './error-report-form.component.html',
  styleUrl: './error-report-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ErrorReportFormComponent {
  /** The incident to report. The form is inert until this is provided. */
  readonly context = input<ErrorReportContext | null>(null);
  readonly showResultTitle = input(true);

  /** Emits once the report has been accepted by the backend. */
  readonly sent = output<ErrorReportResponse>();

  readonly #facade = inject(ErrorReportingFacade);
  readonly #destroyRef = inject(DestroyRef);
  readonly #store = inject(ErrorReportStore);
  readonly #feedbackAuth = inject(ERROR_REPORT_FEEDBACK_AUTH);
  #retryAfter = 0;

  readonly form = new FormGroup({
    comment: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.maxLength(2000)] }),
    email: new FormControl('', { nonNullable: true, validators: [Validators.email, Validators.maxLength(254)] }),
  });

  readonly status = signal<SubmitStatus>('idle');
  readonly resultUrl = signal<string | null>(null);
  readonly errorMessage = signal<string | null>(null);
  readonly detailsExpanded = signal(false);
  readonly isFeedback = computed(() => this.context()?.kind === 'feedback');
  readonly files = signal<readonly ErrorReportAttachment[]>([]);
  readonly filesTooLarge = computed(
    () =>
      this.files().length > MAX_REPORT_FILES ||
      this.files().some(file => file.blob.size > MAX_REPORT_FILE_BYTES) ||
      this.files().reduce((size, file) => size + file.blob.size, 0) > MAX_REPORT_TOTAL_FILE_BYTES
  );
  private readonly result = viewChild<ElementRef<HTMLElement>>('result');

  constructor() {
    effect(() => this.result()?.nativeElement.focus());
    effect(() => {
      this.files.set(this.context()?.operation?.files ?? []);
    });
  }

  /** Preview the same sanitized, bounded diagnostics that will be submitted. */
  readonly diagnosticsPreview = computed(() => {
    const context = this.context();
    return context ? buildErrorReportDiagnostics(context, this.files()) : '';
  });

  toggleDetails(): void {
    this.detailsExpanded.update(expanded => !expanded);
  }

  removeFile(index: number): void {
    if (this.status() !== 'sending') this.files.update(files => files.filter((_, fileIndex) => index !== fileIndex));
  }

  showEmail(): boolean {
    return !this.isFeedback() && !this.#feedbackAuth?.isAuthenticated();
  }

  async submit(): Promise<void> {
    const context = this.context();
    const status = this.status();

    if (!context || !['idle', 'error'].includes(status) || Date.now() < this.#retryAfter) {
      return;
    }

    this.form.controls.email.setValue(this.showEmail() ? this.form.controls.email.value.trim() : '');
    this.form.controls.comment.setValue(this.form.controls.comment.value.trim());
    if (this.form.invalid || this.filesTooLarge()) {
      this.form.markAllAsTouched();
      return;
    }

    this.status.set('sending');
    this.errorMessage.set(null);

    let accessToken: string | undefined;
    try {
      accessToken = this.#feedbackAuth?.isAuthenticated()
        ? ((await this.#feedbackAuth.getAccessToken().catch(() => null)) ?? undefined)
        : undefined;
      if (this.isFeedback() && !accessToken) {
        this.errorMessage.set('ERROR_REPORTING.FEEDBACK.AUTH_REQUIRED');
        this.status.set('error');
        return;
      }
      if (this.#destroyRef.destroyed) return;
      if (!(await this.#store.beginSubmission(context))) {
        if (this.#store.submissionState(context) === 'accepted') {
          this.status.set('reported');
        } else {
          this.#onUncertain(context);
        }
        return;
      }
      if (this.#destroyRef.destroyed) {
        this.#store.allowRetry(context);
        return;
      }
    } catch {
      this.errorMessage.set('ERROR_REPORTING.FORM.FAILED');
      this.status.set('error');
      return;
    }

    const request = buildErrorReportRequest(context, {
      ...this.form.getRawValue(),
      // Private submission is temporarily unavailable in the UI.
      isPrivate: false,
      email: this.showEmail() ? this.form.controls.email.value : undefined,
      files: this.files(),
      reportId: this.#store.getReportId(context),
    });

    this.#facade
      .reportError(request, accessToken)
      .pipe(timeout(REQUEST_TIMEOUT_MS), takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: response => this.#onResponse(response, context),
        error: error => this.#onFailure(error, context),
      });
  }

  #onResponse(response: ErrorReportResponse | null, context: ErrorReportContext): void {
    if (response?.Queued === true || response?.IsSuccess === true) {
      this.#store.markAccepted(context);
      this.resultUrl.set(response.Queued === true ? null : this.#accessibleUrl(response));
      this.status.set(response.Queued === true ? 'queued' : 'success');
      this.sent.emit(response);
      return;
    }
    // Only a validated receipt confirms acceptance; an unrecognized reply is uncertain.
    this.#onUncertain(context);
  }

  #onFailure(error: unknown, context: ErrorReportContext): void {
    const notConfigured =
      error instanceof HttpErrorResponse &&
      error.status === 503 &&
      (error.error?.Code ?? error.error?.code) === 'reporting_not_configured';
    if (
      error instanceof HttpErrorResponse &&
      (notConfigured || [400, 401, 403, 404, 405, 413, 429, 501].includes(error.status))
    ) {
      this.#store.allowRetry(context);
      if (error.status === 429) {
        const retryHeader = error.headers.get('Retry-After');
        const seconds = Number(retryHeader);
        this.#retryAfter =
          Number.isFinite(seconds) && seconds > 0
            ? Date.now() + seconds * 1000
            : Math.max(Date.now() + DEFAULT_RETRY_SECONDS * 1000, Date.parse(retryHeader ?? '') || 0);
      }
      this.errorMessage.set(
        [401, 403].includes(error.status) && this.isFeedback()
          ? 'ERROR_REPORTING.FEEDBACK.AUTH_REQUIRED'
          : notConfigured || [404, 405, 501].includes(error.status)
            ? 'ERROR_REPORTING.FORM.UNAVAILABLE'
            : error.status === 429
              ? 'ERROR_REPORTING.FORM.RATE_LIMITED'
              : 'ERROR_REPORTING.FORM.FAILED'
      );
      this.status.set('error');
    } else {
      this.#onUncertain(context);
    }
  }

  #onUncertain(context: ErrorReportContext): void {
    this.#store.markUncertain(context);
    this.errorMessage.set('ERROR_REPORTING.FORM.UNCERTAIN');
    this.status.set('uncertain');
  }

  #accessibleUrl(response: ErrorReportResponse): string | null {
    if (response.TopicUrlAccessible !== true || !response.Url) return null;
    try {
      const url = new URL(response.Url);
      return url.protocol === 'https:' && !url.username && !url.password ? url.href : null;
    } catch {
      return null;
    }
  }
}
